import { FeatureFlag } from '@ps/web-core-enums';
import {
  canUseDOM,
  integrationTesting,
  matchEmbeddedAndModal,
  stripBaseURL,
  stripPrefix
} from '@ps/web-core-utils';
import { CashierPath } from '@ps/web-pam-types';
import { matchPath } from 'react-router';
import type { Selector } from 'reselect';
import Url from 'url-parse';

import { select } from '../../../index';
import type { Model, RootState, SelectorCreator, Slicer } from '../../types';

export interface LocationState {
  hash: string;
  host: string;
  hostname: string;
  href: string;
  origin: string;
  pathname: string;
  port: string;
  protocol: string;
  query: Url['query'];
}

interface LocationModel
  extends Model<LocationState, typeof reducers, void, void, typeof selectors> {
  reducers: typeof reducers;
  selectors: typeof selectors;
}

export const makeLocationState = (url: Url | string): LocationState => {
  const { auth, password, username, slashes, ...rest } =
    typeof url === 'string' ? new Url(url, true) : url;
  return rest;
};

const reducers = {
  update: (_state: LocationState, payload: Url | string) => makeLocationState(payload)
};

const selectors = (slice: Slicer<LocationState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getHost() {
    return slice((state) => state.host);
  },
  getHostname() {
    return slice((state) => state.hostname);
  },
  getQuery() {
    return slice((state) => state.query);
  },
  getPort() {
    return slice((state) => state.port);
  },
  getProtocol() {
    return slice((state) => state.protocol);
  },
  getRedirectPath() {
    return createSelector(
      slice,
      (rootState: RootState) => select.locale.get(rootState),
      (location, locale) => {
        const url = new Url(location.href, true);
        if (url.query.modal) {
          url.set('host', '');
          url.set('protocol', '');
          url.set('slashes', false);
          delete url.query.mitid;
          delete url.query.modal;

          return url.toString().replace(new RegExp(`^/${locale}(/.*)?$`), '$1');
        }
        return undefined;
      }
    );
  },

  getOrigin() {
    return slice((state) => state.origin);
  },
  getPathname() {
    return slice((state) => state.pathname);
  },
  getHref() {
    return slice((state) => state.href);
  },
  getCookieDomain() {
    return slice((state) =>
      integrationTesting ? 'localhost' : state.hostname.split('.').slice(1).join('.')
    );
  },
  getOneTrustDomain() {
    return slice((state) =>
      integrationTesting ? 'localhost' : state.hostname.replace(/^www\./, '')
    );
  },
  getWC2Domain(this: {
    getCookieDomain: Selector<RootState, string>;
    getPort: Selector<RootState, string>;
  }) {
    return createSelector(
      slice,
      this.getCookieDomain,
      this.getPort,
      (_, hostname, port) =>
        new Url('/', '')
          .set('protocol', 'https')
          .set('hostname', `cashier.${hostname}`)
          .set('port', port).href
    );
  },
  getAnalyticsPath(this: { stripPrefix: Selector<RootState, string> }) {
    return createSelector(
      slice,
      this.stripPrefix,
      (rootState: RootState) => select.modal.getCurrent(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      ({ pathname, hash }, strippedPathname, currentModal, locale) => {
        if (currentModal) return `/modal${currentModal.path}`;

        // tracking handled by WizardGroup
        if (matchPath(strippedPathname, { path: '/(signup|deposit|withdraw)' })) return undefined;

        const helpSearch = '/help/search';
        if (pathname.startsWith(helpSearch)) return helpSearch;
        const localeHelpSearch = `/${locale}${helpSearch}`;
        if (pathname.startsWith(localeHelpSearch)) return localeHelpSearch;

        return `${pathname}${hash}`;
      }
    );
  },
  stripPrefix() {
    return createSelector(
      (rootState: RootState) => select.location.getPathname(rootState),
      // https://github.com/rematch/rematch/issues/966
      // Rematch reselect doesn't infer args properly
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rootState: RootState, pathname: any) => pathname,
      (rootState: RootState) => select.locale.get(rootState),
      (locationPathname, pathname, locale) => stripPrefix(pathname || locationPathname, locale)
    );
  },
  getLocaleURL() {
    return createSelector(
      slice,
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ForceDefaultLocale),
      (rootState: RootState) => select.locale.get(rootState),
      ({ pathname }, forceDefaultLocale, locale) =>
        new RegExp(`^/${locale}(/.*)?$`).test(pathname) || forceDefaultLocale ? `/${locale}` : ''
    );
  },
  isEmbeddedRoute(this: { stripPrefix: Selector<RootState, string> }) {
    return createSelector(this.stripPrefix, (pathname) => pathname.startsWith('/embedded/'));
  },
  isEmbeddedModal(this: { stripPrefix: Selector<RootState, string> }) {
    return createSelector(this.stripPrefix, (pathname) => pathname.startsWith('/embedded/modal/'));
  },
  isEmbeddedPRHubModal(this: { stripPrefix: Selector<RootState, string> }) {
    return createSelector(this.stripPrefix, (pathname) =>
      pathname.startsWith('/embedded/modal/prhub/')
    );
  },
  isEmbeddedAccount(this: { stripPrefix: Selector<RootState, string> }) {
    return createSelector(this.stripPrefix, (pathname) =>
      pathname.startsWith('/embedded/account/')
    );
  },
  isEmbeddedCookieConsent(this: { stripPrefix: Selector<RootState, string> }) {
    return createSelector(this.stripPrefix, (pathname) =>
      pathname.startsWith('/embedded/cookie-consent/')
    );
  },
  isStandAloneRMF(this: { stripPrefix: Selector<RootState, string> }) {
    return createSelector(this.stripPrefix, (pathname) =>
      pathname.includes(CashierPath.RealMoneyForm)
    );
  },
  getBaseURL(this: { getLocaleURL: Selector<RootState, string> }) {
    return createSelector(
      this.getLocaleURL,
      (rootState: RootState) => select.location.getPathname(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (localeURL, pathname, locale) =>
        `${localeURL}${matchEmbeddedAndModal(pathname, locale)}` || undefined
    );
  },
  stripBaseURL(this: { getPathname: Selector<RootState, string> }) {
    return createSelector(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rootState: RootState, pathname: any) => pathname,
      (rootState: RootState) => select.location.getPathname(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (pathname, locationPathname, locale) => stripBaseURL(pathname ?? locationPathname, locale)
    );
  }
});

const initialState: LocationState = canUseDOM
  ? makeLocationState(window.location.href)
  : ({ query: {} } as LocationState);

const location: LocationModel = {
  state: initialState,
  reducers,
  selectors
};

export default location;
